import React, { useContext } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Select from 'react-select';
import kebabCase from 'lodash.kebabcase';
import LazyLoad from 'react-lazyload';
import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import Link from '../link/link';
import { languages } from '../../../data/languages';
import LayoutContext, {
  LayoutContextProps
} from '../../contexts/layoutContext';

import { getProcessedValue } from '../../utils/helpers';

import './footer.scss';

import {
  Drupal_Node,
  Drupal_FieldMenuLinkContentDefaultFooterFieldMarketo,
  Drupal_FieldMenuLinkContentFooterFieldMarketo,
  Drupal_FieldMenuLinkContentEnterpriseFooterFieldMarketo,
  Drupal_ParagraphMarketoForm,
  FooterSocialIconsJsonEdge,
  FooterSocialIconsJson
} from '../../generated/graphql';

export const footerMenus = {
  default: 'default-footer',
  homepage: 'footer'
};

const FOOTER_KEYS = {
  SECURITY: 'security'
};

interface FooterLink {
  label: string;
  sublinks?: FooterLink[];
  fieldKey?: string;
  fieldMarketo?:
    | Drupal_FieldMenuLinkContentFooterFieldMarketo
    | Drupal_FieldMenuLinkContentDefaultFooterFieldMarketo
    | Drupal_FieldMenuLinkContentEnterpriseFooterFieldMarketo;
  fieldKmEventName?: string;
  url: string;
}

interface Props {
  padFooter?: boolean;
  languageSwitcherData?: Drupal_Node[];
  menu: FooterLink[];
  showSecurity?: boolean;
  isVisio?: boolean;
}

const Footer = ({
  padFooter,
  languageSwitcherData,
  menu,
  showSecurity,
  isVisio
}: Props) => {
  const {
    language: { langPrefix, shortLangPrefix, i18nKey },
    marketo: { getMarketoModal }
  } = useContext<LayoutContextProps>(LayoutContext);
  const { allFooterSocialIconsJson, i18nData } = useStaticQuery(
    graphql`
      {
        allFooterSocialIconsJson {
          edges {
            node {
              name
              logo
              url
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_footer_services_contact_sales_phone_number
              }
            }
          }
        }
      }
    `
  );
  const socialIcons = allFooterSocialIconsJson.edges as FooterSocialIconsJsonEdge[];
  const i18n = i18nFunct(i18nData, i18nKey);
  let phoneVisible = '';
  let phoneLink = '';
  const visioQueryParams = '?type=visio';

  const switcherValues = Object.keys(languages)
    .filter(thisLang => thisLang !== 'da' && thisLang !== 'sv')
    .reduce((obj, thisLangPrefix) => {
      const thisObj = obj as { [key: string]: {} };
      const { shortLangPrefix: thisShortLangPrefix } = languages[
        thisLangPrefix
      ];
      const { entityUrl }: any =
        languageSwitcherData!.find(translation => {
          if (translation.status) {
            return translation.entityLanguage!.id === thisLangPrefix;
          }
        }) || {};

      thisObj[thisShortLangPrefix] = {
        value: entityUrl
          ? entityUrl.path
          : `/${thisShortLangPrefix === 'en' ? '' : thisShortLangPrefix}`,
        label: languages[thisLangPrefix].name
      };

      return thisObj;
    }, {}) as { [key: string]: { label: string; value: string } };

  if (shortLangPrefix === 'en') {
    phoneVisible = '(888) 875-1867';
    phoneLink = '+18888751867';
  } else if (
    shortLangPrefix === 'de' ||
    shortLangPrefix === 'fr' ||
    shortLangPrefix === 'nl'
  ) {
    phoneVisible = '+31 202-251-571';
    phoneLink = '+31202251571';
  } else {
    phoneVisible = '+1 (877) 210-9890';
    phoneLink = '+18772109890';
  }

  interface CustomOptionsProps {
    data: {
      value: string;
      label: string;
    };
    isSelected: boolean;
  }

  const customOption = ({ data, isSelected }: CustomOptionsProps) => (
    <Link
      to={data.value}
      className={`language-switcher__link ${
        isSelected ? 'language-switcher__link--selected' : ''
      }`}
    >
      {data.label}
    </Link>
  );

  let showLangSelect = true;
  if (shortLangPrefix === 'sv' || shortLangPrefix === 'da') {
    showLangSelect = false;
  }

  return (
    <footer className={`bottom-footer ${padFooter ? 'cushion' : ''}`}>
      <div
        className="lucid-container lucid-container--flex
                    padding-t-sm padding-b-sm"
      >
        <div className="footer-nav">
          {menu.map(item => {
            return (
              <ul className="footer-links" key={item.label}>
                <li className="footer-links-heading">{item.label}</li>
                {(item.sublinks as FooterLink[]).map(subitem => {
                  if (
                    !showSecurity &&
                    subitem.fieldKey === FOOTER_KEYS.SECURITY
                  ) {
                    return null;
                  }
                  let mktoId: string | null = null;
                  let mktoFields = {};
                  const marketoEntity = subitem.fieldMarketo
                    ? (subitem.fieldMarketo
                        .entity as Drupal_ParagraphMarketoForm)
                    : null;
                  if (marketoEntity) {
                    const match = (marketoEntity.entityTranslations! as Drupal_Node[]).find(
                      ({ entityLanguage }) => entityLanguage!.id === langPrefix
                    );

                    if (match) {
                      const marketoForm = match as Drupal_ParagraphMarketoForm;
                      mktoId = marketoForm.fieldMarketoId
                        ? marketoForm.fieldMarketoId
                        : null;
                      mktoFields = {
                        title: marketoForm.fieldMarketoTitle,
                        body: getProcessedValue(marketoForm.fieldMarketoBody),
                        note: getProcessedValue(marketoForm.fieldMarketoNote)
                      };
                    }
                  }

                  if (mktoId) {
                    return (
                      <li className="footer-links__item" key={subitem.label}>
                        <button
                          className="footer-links__link"
                          type="button"
                          data-km-trigger={`footer-${kebabCase(
                            subitem.fieldKmEventName
                          )}`}
                          onClick={() => getMarketoModal(mktoId!, mktoFields)}
                        >
                          {subitem.label}
                        </button>
                      </li>
                    );
                  }
                  const href =
                    isVisio && subitem.fieldKey === 'pricing'
                      ? `${subitem.url}${visioQueryParams}`
                      : subitem.url;
                  return (
                    <li className="footer-links__item" key={subitem.label}>
                      <Link
                        className="footer-links__link"
                        to={href}
                        data-km-trigger={`footer-${kebabCase(
                          subitem.fieldKmEventName
                        )}`}
                      >
                        {subitem.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </div>
        <div className="footer-right">
          <div className="footer-right__top">
            <ul className="social-links">
              {socialIcons.map(socialIcon => {
                const icon = socialIcon.node as FooterSocialIconsJson;
                return (
                  <li key={icon.name!}>
                    <a href={icon.url!}>
                      <LazyLoad offset={300}>
                        <img src={icon.logo!} alt={icon.name!} />
                      </LazyLoad>
                    </a>
                  </li>
                );
              })}
            </ul>
            {showLangSelect && (
              <Select
                components={{ Option: customOption }}
                options={Object.values(switcherValues)}
                className="language-switcher"
                classNamePrefix="language-switcher"
                value={switcherValues[shortLangPrefix]}
                isSearchable={false}
                menuIsOpen
              />
            )}
          </div>
          <div className="download-links padding-t-sm padding-b-sm">
            <a href="https://itunes.apple.com/us/app/lucidchart-flowchart-diagram-visio-viewer/id611543423?mt=8">
              <LazyLoad offset={300}>
                <img
                  src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/app-store.svg"
                  alt="Apple App Store"
                />
              </LazyLoad>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.lucidchart.android.chart&amp;hl=en">
              <LazyLoad offset={300}>
                <img
                  src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/google-play.svg"
                  alt="Google Play Store"
                />
              </LazyLoad>
            </a>
          </div>

          <div className="footer-copyright padding-t-sm">
            <a
              href={`tel:${phoneLink}`}
              className="contact-sales js-phone-number"
            >
              {i18n('mktg-footer-services-contact-sales-phone-number', {
                service: phoneVisible
              })}
            </a>
            <p>
              <span role="img" aria-label="Copyright">
                &copy;
              </span>
              {` ${new Date().getFullYear()} Lucid Software Inc.`}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
